import { C, soft } from '@parsina/framework/tokens';
import { Button } from '@parsina/framework';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import {
  resolveVisitLayout, isDefaultVisitLayout,
  moveRecordSection, setRecordSectionVisible, setRecordSectionCollapsed,
  setWorkTabVisible, setDefaultWorkTab, setCopilotTabVisible, setDefaultCopilotTab,
} from '@parsina/framework/clinical/src/screens/Visit/visitLayout';

// ─────────────────────────────────────────────────────────────────────────────
// «صفحه ویزیت» — the layout editor, DESKTOP.
//
// The phone has its own (`VisitLayoutEditor.mobile.jsx`), which is the one
// place in this feature where the two surfaces fork. Everything else — the
// catalogue, the resolver, every operation — is shared, so the fork is of the
// SURFACE only: a row here puts three decisions side by side, which 390px
// cannot hold without wrapping and without dropping its ▲/▼ below the 44px
// touch floor. Both editors call the same functions and send the same patches.
//
// It holds no state and no policy — every click maps to one pure operation in
// screens/Visit/visitLayout.js that returns the patch to persist, and
// `settings` is re-read from the server (or the optimistic overlay) after each
// save. The three groups are the three columns of the visit page, in the order
// they sit on screen for a right-to-left reader: the record, the work area,
// the copilot.
//
// Controls are native checkboxes and radios rather than the kit's Toggle row:
// a row here carries TWO booleans and an order, and the Toggle is a full-width
// single-setting row. Native controls keep the keyboard and screen-reader
// contract (§12) without inventing a third switch.
// ─────────────────────────────────────────────────────────────────────────────

const ROW = {
  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
  padding: '10px 0', borderBottom: `1px solid ${C.border}`,
};
const TITLE = { flex: '1 1 160px', minWidth: 0, fontSize: 14, color: C.textPrimary };
const CHECK = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: C.textSecondary, cursor: 'pointer', whiteSpace: 'nowrap' };
const INPUT = { accentColor: C.navy, width: 16, height: 16, margin: 0, cursor: 'pointer' };
const GROUP_TITLE = { margin: '0 0 2px', fontSize: 13, fontWeight: 700, color: C.textSecondary };
const GROUP_HINT = { margin: '0 0 4px', fontSize: 12, color: C.textMuted, lineHeight: 1.7 };
const LOCK_NOTE = { fontSize: 11, color: C.textMuted, marginInlineStart: 6 };

function Group({ title, hint, testId, children }) {
  return (
    <div data-testid={testId} style={{ marginBottom: 18 }}>
      <h3 style={GROUP_TITLE}>{title}</h3>
      {hint && <p style={GROUP_HINT}>{hint}</p>}
      <div>{children}</div>
    </div>
  );
}

function Check({ label, checked, disabled, onChange, testId }) {
  return (
    <label style={{ ...CHECK, opacity: disabled ? 0.55 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        style={INPUT}
      />
      {label}
    </label>
  );
}

function Radio({ name, label, checked, disabled, onChange, testId }) {
  return (
    <label style={{ ...CHECK, opacity: disabled ? 0.55 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
      <input
        type="radio"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange()}
        data-testid={testId}
        style={INPUT}
      />
      {label}
    </label>
  );
}

// ▲ / ▼ — as text glyphs, which mirror correctly under both directions since
// "up" and "down" are not directional in RTL.
function OrderButtons({ id, index, count, onMove, t }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2, flexShrink: 0 }}>
      <Button
        variant="ghost" size="sm" className="h-8 w-8 px-0"
        aria-label={t('settings.visit.moveUp')}
        disabled={index === 0}
        data-testid={`settings-visit-record-${id}-up`}
        onClick={() => onMove(id, -1)}
      >
        <span aria-hidden style={{ fontSize: 11 }}>▲</span>
      </Button>
      <Button
        variant="ghost" size="sm" className="h-8 w-8 px-0"
        aria-label={t('settings.visit.moveDown')}
        disabled={index === count - 1}
        data-testid={`settings-visit-record-${id}-down`}
        onClick={() => onMove(id, +1)}
      >
        <span aria-hidden style={{ fontSize: 11 }}>▼</span>
      </Button>
    </span>
  );
}

/**
 * @param settings  the stored (sparse) `doctor.settings.visitLayout`
 * @param onChange  (patch) => Promise — persists the keys the patch names
 * @param onReset   () => Promise — back to the shipped page
 */
export default function VisitLayoutEditor({ settings, onChange, onReset, testId = 'settings-visit' }) {
  const { t } = useTranslation();
  const layout = resolveVisitLayout(settings);
  const pristine = isDefaultVisitLayout(settings);
  const apply = (patch) => { if (patch && Object.keys(patch).length) onChange(patch); };

  return (
    <div data-testid={testId}>
      {/* ── پرونده بیمار ── */}
      <Group
        testId={`${testId}-record`}
        title={t('settings.visit.record.title')}
        hint={t('settings.visit.record.hint')}
      >
        {layout.recordSections.map((section, index) => (
          <div key={section.id} role="group" aria-label={t(section.labelKey)} style={ROW} data-testid={`${testId}-record-${section.id}`}>
            <OrderButtons id={section.id} index={index} count={layout.recordSections.length} t={t}
              onMove={(id, delta) => apply(moveRecordSection(settings, id, delta))} />
            <span style={{ ...TITLE, opacity: section.visible ? 1 : 0.6 }}>
              {t(section.labelKey)}
              {section.locked && <span style={LOCK_NOTE}>{t('settings.visit.alwaysShown')}</span>}
            </span>
            <Check
              label={t('settings.visit.show')}
              checked={section.visible}
              disabled={Boolean(section.locked)}
              testId={`${testId}-record-${section.id}-visible`}
              onChange={(on) => apply(setRecordSectionVisible(settings, section.id, on))}
            />
            <Check
              label={t('settings.visit.openByDefault')}
              checked={!section.collapsed}
              disabled={!section.visible}
              testId={`${testId}-record-${section.id}-open`}
              onChange={(open) => apply(setRecordSectionCollapsed(settings, section.id, !open))}
            />
          </div>
        ))}
      </Group>

      {/* ── ناحیه کار ── */}
      <Group
        testId={`${testId}-work`}
        title={t('settings.visit.work.title')}
        hint={t('settings.visit.work.hint')}
      >
        {layout.workTabs.map((tab) => (
          <div key={tab.id} role="group" aria-label={t(tab.labelKey)} style={ROW} data-testid={`${testId}-work-${tab.id}`}>
            <span style={{ ...TITLE, opacity: tab.visible ? 1 : 0.6 }}>
              {t(tab.labelKey)}
              {tab.locked && <span style={LOCK_NOTE}>{t('settings.visit.alwaysShown')}</span>}
            </span>
            <Check
              label={t('settings.visit.show')}
              checked={tab.visible}
              disabled={Boolean(tab.locked)}
              testId={`${testId}-work-${tab.id}-visible`}
              onChange={(on) => apply(setWorkTabVisible(settings, tab.id, on))}
            />
            <Radio
              name={`${testId}-work-default`}
              label={t('settings.visit.openFirst')}
              checked={layout.defaultWorkTab === tab.id}
              disabled={!tab.visible}
              testId={`${testId}-work-${tab.id}-default`}
              onChange={() => apply(setDefaultWorkTab(settings, tab.id))}
            />
          </div>
        ))}
      </Group>

      {/* ── دستیار بالینی ── */}
      <Group
        testId={`${testId}-copilot`}
        title={t('settings.visit.copilot.title')}
        hint={t('settings.visit.copilot.hint')}
      >
        {layout.copilotTabs.map((tab) => {
          const lastVisible = tab.visible && layout.copilotTabs.filter((x) => x.visible).length === 1;
          return (
            <div key={tab.id} role="group" aria-label={t(tab.labelKey)} style={ROW} data-testid={`${testId}-copilot-${tab.id}`}>
              <span style={{ ...TITLE, opacity: tab.visible ? 1 : 0.6 }}>{t(tab.labelKey)}</span>
              <Check
                label={t('settings.visit.show')}
                checked={tab.visible}
                disabled={lastVisible}
                testId={`${testId}-copilot-${tab.id}-visible`}
                onChange={(on) => apply(setCopilotTabVisible(settings, tab.id, on))}
              />
              <Radio
                name={`${testId}-copilot-default`}
                label={t('settings.visit.openFirst')}
                checked={layout.defaultCopilotTab === tab.id}
                disabled={!tab.visible}
                testId={`${testId}-copilot-${tab.id}-default`}
                onChange={() => apply(setDefaultCopilotTab(settings, tab.id))}
              />
            </div>
          );
        })}
      </Group>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Button
          variant="outline" size="sm"
          disabled={pristine}
          data-testid={`${testId}-reset`}
          onClick={() => onReset()}
        >
          {t('settings.visit.reset')}
        </Button>
        <span style={{ fontSize: 12, color: C.textMuted }}>
          {pristine ? t('settings.visit.isDefault') : t('settings.visit.isCustom')}
        </span>
      </div>

      {/* The rule the editor enforces, said once so a greyed checkbox is not a mystery. */}
      <p style={{ margin: '12px 0 0', fontSize: 12, color: C.textMuted, lineHeight: 1.7, background: soft(C.navy, 0.04), borderRadius: 8, padding: '8px 12px' }}>
        {t('settings.visit.safetyNote')}
      </p>
    </div>
  );
}
